/** Deterministic integer hash → [0,1). Shared by texture workers and scene layout. */
export function hash(x: number, y: number, s: number): number {
  let n = Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul(s | 0, 1442695041);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  n ^= n >>> 16;
  return (n >>> 0) / 4294967296;
}

/** Value noise, optionally tileable with period P. */
export function vn(x: number, y: number, P: number, s: number): number {
  const xi = Math.floor(x), yi = Math.floor(y), xf = x - xi, yf = y - yi;
  const u = xf * xf * (3 - 2 * xf), w = yf * yf * (3 - 2 * yf);
  const m = (a: number) => (P ? ((a % P) + P) % P : a);
  const a = hash(m(xi), m(yi), s), b = hash(m(xi + 1), m(yi), s);
  const c = hash(m(xi), m(yi + 1), s), d = hash(m(xi + 1), m(yi + 1), s);
  return a + (b - a) * u + (c - a) * w + (a - b - c + d) * u * w;
}

export function fbm(x: number, y: number, P: number, o: number, s: number): number {
  let t = 0, a = 0.5, f = 1, n = 0;
  for (let i = 0; i < o; i++) {
    t += a * vn(x * f, y * f, P ? P * f : 0, s + i * 13);
    n += a; f *= 2; a *= 0.5;
  }
  return t / n;
}

/** Worley noise → [F1, F2, cell id]. */
export function vor(x: number, y: number, s: number): [number, number, number] {
  const gx = Math.floor(x), gy = Math.floor(y);
  let f1 = 9, f2 = 9, id = 0;
  for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) {
    const cx = gx + i, cy = gy + j;
    const px = cx + 0.1 + 0.8 * hash(cx, cy, s), py = cy + 0.1 + 0.8 * hash(cx, cy, s + 7);
    const d = (px - x) ** 2 + (py - y) ** 2;
    if (d < f1) { f2 = f1; f1 = d; id = hash(cx, cy, s + 3); } else if (d < f2) f2 = d;
  }
  return [Math.sqrt(f1), Math.sqrt(f2), id];
}
